import { TriangleAlert } from "lucide-react";
import { memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import ShowInApplicationLink from "@/components/application/aiAnalysis/ShowInApplicationLink";
import BaseTransactionTable, {
  type TableColumn,
} from "@/components/transactions/BaseTransactionTable";
import ExpandableRow from "@/components/transactions/ExpandableRow";
import FunderCard from "@/components/underwriting/FunderCard";
import { cn } from "@/lib/utils";
import type { CorrectionItem } from "@/types/analysisRun";
import type { FunderGroup } from "@/types/underwriting";

import { useIsPhone } from "../chat/PhoneSheet";
import type { BlockPosition, BlockPositionSide, BlockTransaction } from "../protocol/protocol";
import type { AgentState } from "../store/agentSlice";
import { selectChoice } from "../store/selectors";
import { AssistantCard, AssistantCardBody, AssistantCardHeader } from "../theme";
import { THREAD_CARD } from "./ApprovalCard";
import { CARD_TABLE, cardCells, cardColumns, ChangeStack } from "./cardTable";
import {
  DecisionFooter,
  FIGURES_FILL_ROW,
  ImpactSection,
  impactScope,
  LOOKS,
  RowMark,
  useCorrectionImpact,
  useDecide,
  useTicks,
} from "./correctionCard";
import { FigureGrid } from "./FigureGrid";
import { CATEGORY_NAMES, NONE, REVENUE_NAMES } from "./transactionChange";
import { choiceOutcome, positionCard, type PositionEntry, type PositionRow } from "./wire";

export const EXTENDED_HINT = "Make sure this change is correct. This can't be set in the app.";

/**
 * Whether a position counts toward leverage, as the analysis decides it (counts_toward_leverage):
 * an explicit in or out, else an active MCA on a known schedule (`scheduled`, the server's test of
 * the stored frequency, until a ticked row sets one).
 */
function countsInLeverage(kind: BlockPosition["kind"], side: BlockPositionSide) {
  if (side.leverage_override) return side.leverage_override === "in";
  return kind === "mca" && side.scheduled && side.status === "active";
}

/**
 * The position after the ticked field rows alone. The server's `after` is for all of them (and
 * stays so on a card approved for some), so with only some ticked it is `now` with theirs set.
 */
export function positionAfter(position: BlockPosition, ticked: Set<string>): BlockPositionSide {
  const on = position.rows.filter((row) => ticked.has(row.key));
  if (on.length === position.rows.length) return position.after;
  if (!on.length) return position.now;
  const side = { ...position.now };
  for (const row of on) {
    if (row.field === "include_in_leverage") side.leverage_override = row.to ? "in" : "out";
    else if (row.field === "payment_amount") side.amount = String(row.to);
    else if (row.field === "funder_name") side.funder = String(row.to);
    else side[row.field] = String(row.to);
    if (row.field === "frequency") side.scheduled = row.to !== "unknown";
  }
  return { ...side, in_leverage: countsInLeverage(position.kind, side) };
}

/** A side as the dashboard's funder card takes it: one schedule, its funding deposit. */
function funderGroup(position: BlockPosition, side: BlockPositionSide): FunderGroup {
  const funded = Number(side.funded || 0);
  return {
    name: side.funder || position.funder || "Position",
    position_type: position.kind,
    is_active: side.status === "active",
    total_deposits: funded,
    total_withdrawn: 0,
    deposits: funded && side.funding_date ? [{ date: side.funding_date, amount: funded }] : [],
    withdrawals: side.amount
      ? [
          {
            amount: Number(side.amount),
            count: side.payments,
            frequency: side.frequency,
            is_active: side.status === "active",
            include_in_leverage: side.in_leverage,
            tags: [],
          },
        ]
      : [],
  };
}

const ignore = () => {};

/** One side of the position, read-only: what the dashboard's Existing Positions shows. */
function PositionSide({
  label,
  position,
  side,
}: {
  label: string;
  position: BlockPosition;
  side: BlockPositionSide;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1" data-side={label}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {label}
        <span className="ml-1.5 font-normal normal-case tracking-normal">
          {side.in_leverage ? "In leverage" : "Not in leverage"}
        </span>
      </span>
      <div className="pointer-events-none overflow-hidden rounded-md border">
        <FunderCard
          funder={funderGroup(position, side)}
          isSelected={false}
          onNameClick={ignore}
          onHighlightDates={ignore}
          onToggleActive={ignore}
          calendarTransactions={[]}
          withdrawalActiveOverrides={{}}
          activeToggleDisabled
          frequencyDisabled
        />
      </div>
    </div>
  );
}

/** The corrections the ticked rows make, as the preview endpoint takes them. */
function correctionItems(card: { positions: PositionEntry[] }, ticked: Set<string>) {
  return card.positions.flatMap(({ position, rows }) =>
    rows
      .filter((row) => ticked.has(row.key))
      .map(
        (row): CorrectionItem => ({
          target_type: "position",
          target_key: position.key,
          field: row.change.field,
          value: row.change.to,
        })
      )
  );
}

/**
 * An approval whose rows are a position's fields (correct_position): the analysis's top row as it
 * would be, over what it was, then each position: its field rows, each ticked, the position now
 * and after as the dashboard's funder card, and the transactions it is built from. Ticking fewer
 * asks the server what those alone would change; approving sends the ticked rows. The web's own
 * card: the MCP approval view draws the same blocks as plain HTML.
 */
export const PositionApprovalCard = memo(function PositionApprovalCard({
  choiceRef,
}: {
  choiceRef: string;
}) {
  const choice = useSelector((state: { agent: AgentState }) => selectChoice(state, choiceRef));
  const card = useMemo(() => (choice ? positionCard(choice) : null), [choice]);
  const phone = useIsPhone();
  // One open transaction for the card, by position: a row two positions share opens in one.
  const [expanded, setExpanded] = useState<string | null>(null);
  const { pressed, busy, error, setError, press } = useDecide(choiceRef);

  const outcome = choice ? choiceOutcome(choice) : "superseded";
  const pending = outcome === "pending";
  const rows = useMemo(() => card?.rows ?? [], [card]);
  const { proposed, ticked, narrowed, toggle } = useTicks(rows, pending);
  const chosen = rows.filter((row) => ticked.has(row.key));
  // One string for the ticked set, so the preview's items keep their identity between renders.
  const tickedKey = narrowed ? chosen.map((row) => row.key).join(",") : "";
  const items = useMemo(
    () => (tickedKey && card ? correctionItems(card, new Set(tickedKey.split(","))) : []),
    [tickedKey, card]
  );
  const { impact, failed, updating } = useCorrectionImpact(card, {
    narrowed,
    nothingTicked: !chosen.length,
    items,
  });
  if (!choice || !card) return null;

  const look = LOOKS[outcome];
  const tickable = pending && !busy;
  const scope = impactScope({
    outcome,
    narrowed,
    failed,
    chosen: chosen.length,
    proposed: proposed.length,
    offered: rows.length,
    stats: card.stats,
  });
  const tick = (key: string) => {
    toggle(key);
    setError(null);
  };

  const fieldRow = (row: PositionRow) => {
    const on = ticked.has(row.key);
    const settled = outcome === "approved" && on ? row.state : null;
    const { change } = row;
    return (
      <li
        key={row.key}
        data-key={row.key}
        data-ticked={on}
        className="flex flex-col gap-0.5 py-1 text-sm"
      >
        <span className="flex items-center gap-2">
          <RowMark
            state={settled}
            checked={on}
            onChange={() => tick(row.key)}
            label={row.disabled ? `${change.label}, ${row.disabled}` : change.label}
            disabled={!tickable || !!row.disabled}
          />
          <span className={cn("inline-flex items-center gap-1 font-medium", !on && "opacity-50")}>
            {change.label}
            {change.extended && (
              <span role="img" aria-label={EXTENDED_HINT} title={EXTENDED_HINT}>
                <TriangleAlert
                  aria-hidden
                  className="h-3.5 w-3.5 text-amber-600 dark:text-amber-500"
                />
              </span>
            )}
          </span>
          <span
            className={cn(
              "ml-auto inline-flex flex-wrap items-baseline justify-end gap-x-1",
              !on && "opacity-50"
            )}
          >
            <s className="text-muted-foreground">{change.old || NONE}</s>
            <span aria-hidden className="text-muted-foreground">
              →
            </span>
            <span className="font-semibold text-primary">{change.new || NONE}</span>
          </span>
        </span>
        {/* A field the call no longer has (the card reopened after an approve failed). */}
        {pending && row.disabled && (
          <span className="pl-6 text-xs text-muted-foreground">Can't tick: {row.disabled}</span>
        )}
        {settled && row.line && (
          <span className="pl-6 text-xs text-muted-foreground">{row.line}</span>
        )}
      </li>
    );
  };

  const columns: TableColumn<BlockTransaction>[] = cardColumns<BlockTransaction>(phone, {
    label: "Category",
  });
  const transactionRow = (positionKey: string, transaction: BlockTransaction) => {
    const open = `${positionKey}:${transaction.key}`;
    return (
      <ExpandableRow
        key={transaction.key}
        isExpanded={expanded === open}
        onToggle={() => setExpanded((current) => (current === open ? null : open))}
        cells={cardCells(
          phone,
          transaction,
          <ChangeStack category={transaction.category} revenue={transaction.revenue} />
        )}
        detail={{
          type: "fields",
          fields: [
            { label: "Description:", value: transaction.description || NONE },
            { label: "Memo:", value: transaction.memo || NONE },
            {
              label: "Account:",
              value: transaction.account_last4 ? `…${transaction.account_last4}` : NONE,
            },
            {
              label: "Category:",
              value: CATEGORY_NAMES[transaction.category.new] ?? (transaction.category.new || NONE),
            },
            { label: "Revenue:", value: REVENUE_NAMES[transaction.revenue.new] ?? NONE },
            ...(transaction.count > 1
              ? [{ label: "Identical:", value: `${transaction.count} transactions` }]
              : []),
          ],
        }}
        compact
      />
    );
  };

  const positionSection = ({ group, position, rows: own }: PositionEntry) => {
    const { counts } = position;
    const parts = [
      counts.payments ? `${counts.payments} ${counts.payments === 1 ? "payment" : "payments"}` : "",
      counts.fundings ? `${counts.fundings} ${counts.fundings === 1 ? "funding" : "fundings"}` : "",
      counts.returns ? `${counts.returns} returned` : "",
    ].filter(Boolean);
    const shown =
      position.transactions.length < counts.transactions
        ? `, the newest ${position.transactions.length} shown`
        : "";
    return (
      <section
        key={position.key}
        aria-label={group}
        className="flex min-w-0 flex-col gap-2"
        data-position={position.key}
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {group}
        </p>
        <ul className="flex flex-col divide-y">{own.map(fieldRow)}</ul>
        {/* Now beside After only when the card itself has room for two funder cards (about 260px
            each); on a narrower card, desktop or phone, Now stacks over After. */}
        <div className="[container:position-sides/inline-size]">
          <div className="grid gap-2 [@container_position-sides_(min-width:36rem)]:grid-cols-2">
            <PositionSide label="Now" position={position} side={position.now} />
            <PositionSide
              label="After"
              position={position}
              side={positionAfter(position, ticked)}
            />
          </div>
        </div>
        {position.transactions.length > 0 && (
          <div className={CARD_TABLE}>
            <BaseTransactionTable
              dense
              columns={columns}
              items={position.transactions}
              renderRow={(transaction) => transactionRow(position.key, transaction)}
              defaultRowsPerPage={10}
            />
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {counts.transactions
              ? `${counts.transactions} transactions${parts.length ? ` · ${parts.join(", ")}` : ""}${shown}`
              : "No transactions"}
          </span>
          <ShowInApplicationLink
            target={{
              applicationId: card.applicationId,
              run: card.runId,
              filter: { kind: "position", position: position.key },
            }}
          />
        </div>
      </section>
    );
  };

  return (
    <AssistantCard className={THREAD_CARD} data-outcome={outcome} data-choice-ref={choiceRef}>
      <AssistantCardHeader
        className="items-start"
        icon={look.icon}
        tone={look.tone}
        title={<span className="whitespace-normal break-words">{choice.prompt}</span>}
      />
      <AssistantCardBody className="flex flex-col gap-3">
        {choice.summary && <p className="text-muted-foreground">{choice.summary}</p>}
        {card.figures.length > 0 && (
          <FigureGrid figures={card.figures} className={FIGURES_FILL_ROW} />
        )}
        <ImpactSection
          group={card.stats?.group}
          impact={impact}
          scope={scope}
          updating={updating}
          dimmed={outcome === "declined" || outcome === "superseded"}
          hideDecision={narrowed && !chosen.length}
          failedNote={
            failed
              ? `Couldn't work out the figures for the selected changes; showing them for all ${proposed.length}.`
              : null
          }
        />
        {card.positions.map(positionSection)}
        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </AssistantCardBody>
      <DecisionFooter
        choice={choice}
        outcome={outcome}
        pressed={pressed}
        chosen={chosen.length}
        narrowed={narrowed}
        onPress={(optionId) => press(optionId, narrowed ? chosen.map((row) => row.key) : undefined)}
      />
    </AssistantCard>
  );
});
